/**
 * CR500 Phase 4B/4C: Promo Badges Helper
 * 
 * Single source of truth for computing which promo badges a deal should display.
 * Used by both V1 and V2 renderers to ensure parity.
 * 
 * Phase 4C additions:
 * - maxBadges: limit badge count
 * - order: explicit ordering control
 * - Debug diagnostics for corporate detection
 */

import type { Deal, DealCategory } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";
import { hasMissingDetails, isBulkDeal, needsConfirmOnSite } from "@/lib/types/deals";

// ================================================================
// TYPES
// ================================================================

export type PromoBadgeKind = 
  | "membership"
  | "corporate"
  | "unclassified"
  | "bulk"
  | "details_vary"
  | "confirm_on_site";

export interface PromoBadge {
  /** Unique key for this badge type */
  key: PromoBadgeKind;
  /** Display label */
  label: string;
  /** Icon hint for rendering */
  icon: "lock" | "building" | "help" | "info" | "alert";
  /** Color variant */
  variant: "amber" | "purple" | "slate" | "muted";
}

export interface PromoBadgesVisibilityConfig {
  /** Master enable/disable for all badges */
  enabled?: boolean;
  /** Individual badge toggles */
  showMembership?: boolean;
  showCorporate?: boolean;
  showUnclassified?: boolean;
  showBulkDeal?: boolean;
  showDetailsVary?: boolean;
  showConfirmOnSite?: boolean;
  /** Maximum number of badges to display (Phase 4C) */
  maxBadges?: number;
  /** Explicit ordering of badge keys (Phase 4C) */
  order?: PromoBadgeKind[];
}

/**
 * Default ordering of badges (most important first).
 */
export const DEFAULT_BADGE_ORDER: PromoBadgeKind[] = [
  "membership",
  "corporate",
  "unclassified",
  "bulk",
  "details_vary",
  "confirm_on_site",
];

// ================================================================
// CORPORATE DETECTION (normalized) + DEBUG DIAGNOSTICS
// ================================================================

export interface CorporateDebugInfo {
  /** Whether deal is classified as corporate */
  isCorporate: boolean;
  /** Which signals were detected */
  signals: {
    eligibilityScope: boolean;
    audienceHint: boolean;
    isCorporateDeal: boolean;
    corporateOnly: boolean;
    audienceCorporate: boolean;
  };
  /** Raw field values for debugging */
  rawValues: Record<string, unknown>;
}

/**
 * Get detailed corporate detection info for debugging.
 * Used in ?dealCardV2debug=1 mode to diagnose badge issues.
 */
export function getCorporateDebugInfo(deal: Deal): CorporateDebugInfo {
  const legacyDeal = deal as unknown as Record<string, unknown>;
  
  const signals = {
    eligibilityScope: deal.eligibility_scope === "CORPORATE",
    audienceHint: deal.audience_hint === "corporate_bulk",
    isCorporateDeal: legacyDeal.is_corporate_deal === true,
    corporateOnly: legacyDeal.corporate_only === true,
    audienceCorporate: legacyDeal.audience === "corporate",
  };
  
  return {
    isCorporate: Object.values(signals).some(Boolean),
    signals,
    rawValues: {
      eligibility_scope: deal.eligibility_scope,
      audience_hint: deal.audience_hint,
      is_corporate_deal: legacyDeal.is_corporate_deal,
      corporate_only: legacyDeal.corporate_only,
      audience: legacyDeal.audience,
    },
  };
}

/**
 * Canonical corporate deal detection.
 * Supports multiple field variants for backward compatibility.
 */
export function isCorporateDeal(deal: Deal): boolean {
  return getCorporateDebugInfo(deal).isCorporate;
}

// ================================================================
// MEMBERSHIP DETECTION
// ================================================================

/**
 * Get membership badge label if the deal requires access.
 */
function getMembershipLabel(deal: Deal): string | null {
  if (!deal.access_requirement_type || deal.access_requirement_type === "NONE") {
    return null;
  }
  
  switch (deal.access_requirement_type) {
    case "MEMBERSHIP_REQUIRED":
      return deal.access_requirement_program_name 
        ? `${deal.access_requirement_program_name} members`
        : "Members only";
    case "LOGIN_REQUIRED":
      return "Login required";
    case "SIGNUP_REQUIRED":
      return "Sign-up required";
    default:
      return null;
  }
}

// ================================================================
// BADGE ELIGIBILITY (before config filtering)
// ================================================================

export interface BadgeEligibility {
  /** All badges the deal is eligible for (before config filtering) */
  eligible: PromoBadge[];
  /** Debug info for each badge */
  debug: Record<PromoBadgeKind, { eligible: boolean; reason: string }>;
}

/**
 * Compute all badges a deal is eligible for (before config filtering).
 * Useful for debugging and parity checks.
 */
export function getBadgeEligibility(deal: Deal): BadgeEligibility {
  const { category: dealCategory } = getDealType(deal);
  const eligible: PromoBadge[] = [];
  const debug: BadgeEligibility["debug"] = {
    membership: { eligible: false, reason: "No access requirement" },
    corporate: { eligible: false, reason: "Not corporate" },
    unclassified: { eligible: false, reason: "Has classification" },
    bulk: { eligible: false, reason: "Not bulk deal" },
    details_vary: { eligible: false, reason: "Details complete" },
    confirm_on_site: { eligible: false, reason: "No site confirmation needed" },
  };
  
  // 1. Membership badge
  const membershipLabel = getMembershipLabel(deal);
  if (membershipLabel) {
    eligible.push({
      key: "membership",
      label: membershipLabel,
      icon: "lock",
      variant: "amber",
    });
    debug.membership = { eligible: true, reason: `Access: ${deal.access_requirement_type}` };
  }
  
  // 2. Corporate badge
  const corporateInfo = getCorporateDebugInfo(deal);
  if (corporateInfo.isCorporate) {
    eligible.push({
      key: "corporate",
      label: "Corporate",
      icon: "building",
      variant: "purple",
    });
    const activeSignals = Object.entries(corporateInfo.signals)
      .filter(([, v]) => v)
      .map(([k]) => k);
    debug.corporate = { eligible: true, reason: `Signals: ${activeSignals.join(", ")}` };
  }
  
  // 3. Unclassified badge
  if (dealCategory === "UNCLASSIFIED") {
    eligible.push({
      key: "unclassified",
      label: "Unclassified",
      icon: "help",
      variant: "slate",
    });
    debug.unclassified = { eligible: true, reason: "Deal category is UNCLASSIFIED" };
  }
  
  // 4. Bulk deal badge
  if (isBulkDeal(deal)) {
    eligible.push({
      key: "bulk",
      label: "Bulk deal",
      icon: "info",
      variant: "muted",
    });
    debug.bulk = { eligible: true, reason: "isBulkDeal() returned true" };
  }
  
  // 5. Details vary / missing details badge
  if (hasMissingDetails(deal)) {
    eligible.push({
      key: "details_vary",
      label: "Details vary",
      icon: "info",
      variant: "muted",
    });
    debug.details_vary = { eligible: true, reason: "hasMissingDetails() returned true" };
  }
  
  // 6. Confirm on site badge
  if (needsConfirmOnSite(deal)) {
    eligible.push({
      key: "confirm_on_site",
      label: "Confirm on site",
      icon: "alert",
      variant: "amber",
    });
    debug.confirm_on_site = { eligible: true, reason: "needsConfirmOnSite() returned true" };
  }
  
  return { eligible, debug };
}

// ================================================================
// MAIN HELPER
// ================================================================

export interface PromoBadgesResult {
  /** Final badges to display (after config filtering) */
  badges: PromoBadge[];
  /** All eligible badges (before filtering) */
  eligibleBadges: PromoBadge[];
  /** Badges suppressed by config */
  suppressedBadges: Array<{ badge: PromoBadge; reason: string }>;
  /** Debug diagnostics */
  debug: BadgeEligibility["debug"];
}

/**
 * Compute promo badges with full diagnostics.
 * Returns badges to display plus debug info about suppressed badges.
 */
export function getPromoBadgesWithDiagnostics(
  deal: Deal,
  config: PromoBadgesVisibilityConfig = {}
): PromoBadgesResult {
  const {
    enabled = true,
    showMembership = true,
    showCorporate = true,
    showUnclassified = true,
    showBulkDeal = true,
    showDetailsVary = true,
    showConfirmOnSite = true,
    maxBadges,
    order = DEFAULT_BADGE_ORDER,
  } = config;
  
  // Get all eligible badges
  const { eligible: eligibleBadges, debug } = getBadgeEligibility(deal);
  
  // Master disable
  if (!enabled) {
    return {
      badges: [],
      eligibleBadges,
      suppressedBadges: eligibleBadges.map(b => ({ badge: b, reason: "Master toggle disabled" })),
      debug,
    };
  }
  
  // Config toggle map
  const toggleMap: Record<PromoBadgeKind, boolean> = {
    membership: showMembership,
    corporate: showCorporate,
    unclassified: showUnclassified,
    bulk: showBulkDeal,
    details_vary: showDetailsVary,
    confirm_on_site: showConfirmOnSite,
  };
  
  // Filter by config toggles
  const suppressedBadges: PromoBadgesResult["suppressedBadges"] = [];
  let filteredBadges = eligibleBadges.filter(badge => {
    const isEnabled = toggleMap[badge.key];
    if (!isEnabled) {
      suppressedBadges.push({ badge, reason: `Toggle disabled: show${badge.key.charAt(0).toUpperCase() + badge.key.slice(1).replace(/_([a-z])/g, (_, c) => c.toUpperCase())}` });
      return false;
    }
    return true;
  });
  
  // Apply explicit ordering
  filteredBadges = filteredBadges.sort((a, b) => {
    const aIndex = order.indexOf(a.key);
    const bIndex = order.indexOf(b.key);
    // Items not in order go to end
    const aPos = aIndex === -1 ? order.length : aIndex;
    const bPos = bIndex === -1 ? order.length : bIndex;
    return aPos - bPos;
  });
  
  // Apply maxBadges limit
  if (maxBadges !== undefined && maxBadges > 0 && filteredBadges.length > maxBadges) {
    const truncated = filteredBadges.slice(maxBadges);
    truncated.forEach(badge => {
      suppressedBadges.push({ badge, reason: `Truncated: maxBadges=${maxBadges}` });
    });
    filteredBadges = filteredBadges.slice(0, maxBadges);
  }
  
  return {
    badges: filteredBadges,
    eligibleBadges,
    suppressedBadges,
    debug,
  };
}

/**
 * Compute all promo badges for a deal (simple interface).
 * 
 * @param deal - The deal to compute badges for
 * @param config - Optional visibility configuration for filtering badges
 * @returns Array of badges to display, in stable order
 */
export function getPromoBadges(
  deal: Deal,
  config: PromoBadgesVisibilityConfig = {}
): PromoBadge[] {
  return getPromoBadgesWithDiagnostics(deal, config).badges;
}

/**
 * Get badge count for parity checking.
 */
export function getPromoBadgeCount(deal: Deal): number {
  return getPromoBadges(deal).length;
}

/**
 * Check if a specific badge type should show for a deal.
 */
export function hasBadge(deal: Deal, badgeKey: PromoBadgeKind): boolean {
  return getPromoBadges(deal).some(b => b.key === badgeKey);
}
